// Expansion Packs panel (BP5) — vendor-applied pool increments on a license.
//
// Lists active + historical packs, lets an operator add a pack (type/qty/price)
// or expire an active one. Creating/expiring a pack changes the effective pool the
// install's next phone-home returns (the server reconcile sums active packs).
import React, { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { PackagePlus, X } from 'lucide-react';

interface ExpansionPack {
  id: string;
  license_id: string;
  pack_type: 'vendor' | 'ciic_customer' | 'org' | 'ciic_activation';
  quantity: number;
  unit_price_monthly: number;
  applied_at: string;
  active: boolean;
  deactivated_reason?: string | null;
}

const PACK_LABELS: Record<string, string> = {
  vendor: 'Vendor',
  ciic_customer: 'CIIC Customer',
  org: 'Org',
  ciic_activation: 'CIIC Activation',
};

export function ExpansionPacks({ licenseId, readOnly = false }: { licenseId: string; readOnly?: boolean }) {
  const { toast } = useToast();
  const [packs, setPacks] = useState<ExpansionPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [packType, setPackType] = useState<'vendor' | 'ciic_customer' | 'org'>('vendor');
  const [quantity, setQuantity] = useState('10');
  const [price, setPrice] = useState('0');
  const [formError, setFormError] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await apiRequest<ExpansionPack[]>(`/licenses/${licenseId}/packs`);
      // The endpoint normally returns an array, but an error/object payload would
      // otherwise be set verbatim and blow up packs.filter/.map at render. Coerce.
      setPacks(Array.isArray(data) ? data : []);
    } catch {
      setPacks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  const addPack = async () => {
    if (readOnly || busy) return;
    const qty = parseInt(quantity, 10);
    if (!Number.isInteger(qty) || qty <= 0) {
      setFormError('Quantity must be a positive whole number.');
      return;
    }
    const unitPrice = parseFloat(price);
    setBusy('add');
    setFormError(null);
    try {
      await apiRequest(`/licenses/${licenseId}/packs`, {
        method: 'POST',
        body: JSON.stringify({
          pack_type: packType,
          quantity: qty,
          unit_price_monthly: Number.isFinite(unitPrice) ? unitPrice : 0,
        }),
      });
      toast({ title: `Added ${qty} ${PACK_LABELS[packType]} pack — pool grows on next phone-home.` });
      setAdding(false);
      setQuantity('10');
      setPrice('0');
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add pack.');
    } finally {
      setBusy(null);
    }
  };

  const expirePack = async (pack: ExpansionPack) => {
    if (readOnly || busy) return;
    setBusy(pack.id);
    try {
      await apiRequest(`/licenses/${licenseId}/packs/${pack.id}/expire`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'expired by operator' }),
      });
      toast({ title: `Expired ${pack.quantity} ${PACK_LABELS[pack.pack_type]} pack — pool shrinks on next phone-home.` });
      await load();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to expire pack.', variant: 'destructive' as any });
    } finally {
      setBusy(null);
    }
  };

  // Defensive: never trust `packs` to be an array at render time (a non-array
  // slipping through state would crash .filter/.length/.map). One guard, reused below.
  const list = Array.isArray(packs) ? packs : [];

  // Effective add-on capacity contributed by active packs, per type.
  const activeByType = list
    .filter((p) => p.active)
    .reduce<Record<string, number>>((acc, p) => {
      acc[p.pack_type] = (acc[p.pack_type] || 0) + p.quantity;
      return acc;
    }, {});

  return (
    <div className="card" style={{ marginTop: '16px' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title">Expansion Packs</span>
        {!readOnly && !adding && (
          <button
            onClick={() => { setAdding(true); setFormError(null); }}
            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--accent)', background: 'var(--accent)', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <PackagePlus size={14} /> Add Pack
          </button>
        )}
      </div>

      <div className="card-body">
        {Object.keys(activeByType).length > 0 && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {Object.entries(activeByType).map(([type, qty]) => (
              <span key={type} style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '9999px', background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontWeight: 600 }}>
                +{qty} {PACK_LABELS[type]}
              </span>
            ))}
          </div>
        )}

        {!readOnly && adding && (
          <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', marginBottom: '12px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Type
                <select value={packType} onChange={(e) => setPackType(e.target.value as any)} style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }}>
                  <option value="vendor">Vendor</option>
                  <option value="ciic_customer">CIIC Customer</option>
                  <option value="org">Org</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Quantity
                <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', width: '90px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Price / mo (unit)
                <input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', width: '110px' }} />
              </label>
              <button onClick={addPack} disabled={busy === 'add'} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'white', cursor: 'pointer', fontSize: '13px' }}>
                {busy === 'add' ? 'Adding…' : 'Confirm'}
              </button>
              <button onClick={() => { setAdding(false); setFormError(null); }} style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px' }}>
                Cancel
              </button>
            </div>
            {formError && <div style={{ marginTop: '8px', fontSize: '12px', color: '#DC2626' }}>{formError}</div>}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>Loading packs…</div>
        ) : list.length === 0 ? (
          <div style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>No expansion packs applied. The pool equals the base grant.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Type', 'Qty', 'Price/mo', 'Applied', 'Status', ''].map((h) => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', opacity: p.active ? 1 : 0.55 }}>
                  <td style={{ padding: '8px 12px', fontSize: '13px' }}>{PACK_LABELS[p.pack_type] || p.pack_type}</td>
                  <td style={{ padding: '8px 12px', fontSize: '13px', fontWeight: 600 }}>{p.quantity}</td>
                  <td style={{ padding: '8px 12px', fontSize: '13px' }}>${Number(p.unit_price_monthly).toFixed(2)}</td>
                  <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)' }}>{new Date(p.applied_at).toLocaleDateString()}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {p.active
                      ? <span style={{ fontSize: '12px', color: '#16A34A', fontWeight: 600 }}>Active</span>
                      : <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Expired</span>}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {!readOnly && p.active && (
                      <button onClick={() => expirePack(p)} disabled={busy === p.id} title="Expire pack" style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626', cursor: 'pointer', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <X size={12} /> {busy === p.id ? '…' : 'Expire'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
